import { AfterViewInit, Component, OnDestroy, OnInit, ViewEncapsulation, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';

import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
    FuseNavigationItem
} from '@fuse/components/navigation';

import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';

import { DataService } from '@app/core/services/data.service';
import { BadisOauthLoginService } from '@app/core/services/auth/badis-oauth-login.service';
import { UserAccessToken } from '@app/core/services/auth/model/auth-user.interface';
import { NetworkStatusService } from '@app/shared/services/network-status.service';
import { NotificationService } from '@app/pages/notifications/services/notification.service';
import { BackgroundService } from '@app/shared/components/background/background.service';
import { GlobalVariablesService } from '@app/core/services/global-variables.service';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FrostMenuItem, MenuSeparator, createMenuSeparator } from '@app/shared/components/frost-overlay-menu';
import { LanguageService } from '@app/shared/services/language.service';
import { FrostMenuManagerService } from 'app/shared/services/frost-menu-manager.service';
import { ServiceName, ServiceReadyStateService } from '@app/core/services/service-ready-state.service';
import { SystemMenuService } from '@app/core/services/system-menu.services';
import { TranslateService } from '@ngx-translate/core';
import { StyleManagerService } from '@app/core/services/style-manager-service.service';
import { SettingsService } from '@app/core/services/settings/settings.service';

// Dock popup menü öğelerini tanımlamak için interface
interface DockMenuItem {
    id: string;
    icon: IconName;
    label: string;
    route?: string;
    action?: () => void;
    color?: string;
}

// Dock işlemleri için enum
enum DockOperation {
    TOGGLE_MENU_STYLE = 'menu-style',
    TOGGLE_AUTO_HIDE = 'auto-hide',
    TOGGLE_POSITION = 'dock-position',
    TOGGLE_FULLSCREEN = 'fullscreen'
}

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    styleUrls: ['./classy.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ClassyLayoutComponent implements OnInit, AfterViewInit, OnDestroy {

    scrollbarVisibilityBugFix: boolean = false;

    isScreenSmall: boolean;
    navigation: Navigation;
    public user: UserAccessToken;

    // Dock aktiflik durumu
    isDockActive: boolean = false;
    dockHideTimeout: any;

    // Hover sorunlarını çözmek için ek değişken
    isHovering: boolean = false;
    resetOverlayTimeout: any;

    // Menü açılma stili (sidebar veya popup)
    isMenuPopup: boolean = true;

    // Dock popup menü durumu
    activeDockMenu: string | null = null;
    dockMenuItems: { [key: string]: DockMenuItem[] } = {};

    public displayName: string;
    public userName: string;
    public avatar: any;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    // Ana navigasyon bileşenine referans
    @ViewChild('mainNavigation')
    mainNavigation: FuseVerticalNavigationComponent;

    // Ayarlar menüsüne referans
    @ViewChild('settingsMenu')
    settingsMenu: any; // FrostOverlayMenuComponent tipini kullanabilirsiniz

    notificationData$ = this.notificationService.getNotificationsData();
    navigationOpened: boolean = false;
    fixedNavbar: boolean;
    isDockAtBottom: boolean = false;
    isDockAutoHide: boolean = true; // Varsayılan olarak auto-hide açık


    // Ayarlar menüsü için değişkenler - settingsFrostMenuItems kaldırıldı

    // FrostOverlayMenu için grafik menü öğeleri
    chartsMenuItems: (FrostMenuItem | MenuSeparator)[] = [
        {
            id: 'line',
            label: 'Çizgi Grafik',
            icon: ['fal', 'chart-line'],
            description: 'Zaman içindeki değişimleri gösteren çizgi grafikler',
            link: '/charts/line'
        },
        {
            id: 'bar',
            label: 'Çubuk Grafik',
            icon: ['fal', 'chart-bar'],
            description: 'Karşılaştırma için ideal çubuk grafikler',
            link: '/charts/bar'
        },
        createMenuSeparator('Diğer Grafikler'),
        {
            id: 'pie',
            label: 'Pasta Grafik',
            icon: ['fal', 'chart-pie'],
            description: 'Dağılım oranlarını gösteren grafikler',
            link: '/charts/pie'
        },
        {
            id: 'area',
            label: 'Alan Grafik',
            icon: ['fal', 'chart-area'],
            description: 'Biriken değerleri gösteren alan grafikler',
            link: '/charts/area'
        },
        {
            id: 'scatter',
            label: 'Dağılım Grafik',
            icon: ['fal', 'chart-scatter'],
            description: 'İki değişken arasındaki ilişkiyi gösteren grafikler',
            link: '/charts/scatter'
        },
        {
            id: 'advanced',
            label: 'Gelişmiş Grafikler',
            icon: ['fal', 'analytics'],
            description: 'Özel veri analizleri için grafikler',
            children: [
                {
                    id: 'heatmap',
                    label: 'Isı Haritası',
                    icon: ['fal', 'fire'],
                    description: 'Yoğunluk seviyelerini gösteren ısı haritaları',
                    link: '/charts/advanced/heatmap'
                },
                {
                    id: 'radar',
                    label: 'Radar Grafik',
                    icon: ['fal', 'radar'],
                    description: 'Çok boyutlu veriyi gösteren radar grafikler',
                    link: '/charts/advanced/radar'
                },
                {
                    id: 'bubble',
                    label: 'Kabarcık Grafik',
                    icon: ['fal', 'circle'],
                    description: 'Üç değişkeni aynı anda gösteren grafikler',
                    link: '/charts/advanced/bubble'
                }
            ]
        }
    ];


    // Kullanıcı menüsü için menü öğeleri
    userMenuItems: (FrostMenuItem | MenuSeparator)[] = [];

    // Kullanıcı menü öğelerini yükle
    private _loadUserMenuItems(): void {
        this.userMenuItems = [
            {
                id: 'profile',
                label: this.translateService.instant('SystemAdmin.Common.Profile.MyProfile'),
                icon: ['fal', 'user-circle'],
                description: this.translateService.instant('SystemAdmin.Common.Profile.ViewProfile'),
                link: '/profile'
            },
            createMenuSeparator(''),
            {
                id: 'settings',
                label: 'Ayarlar',
                icon: ['fal', 'cog'],
                description: 'Sistem ayarlarını görüntüle',
                link: '/settings'
            }
        ];
    }

    // Dil menüsü için öğeler
    languageMenuItems: (FrostMenuItem | MenuSeparator)[] = [];

    // Raporlar menüsü için öğeler
    reportsFrostMenuItems: (FrostMenuItem | MenuSeparator)[] = [];

    // External Apps menüsü için öğeler
    externalAppsFrostMenuItems: (FrostMenuItem | MenuSeparator)[] = [];

    constructor(
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _badisOauthLoginService: BadisOauthLoginService,
        private dataService: DataService,
        public networkStatusService: NetworkStatusService,
        private notificationService: NotificationService,
        public backgroundService: BackgroundService,
        private _router: Router,
        public globals: GlobalVariablesService,
        private _changeDetectorRef: ChangeDetectorRef,
        public languageService: LanguageService,
        private _frostMenuManager: FrostMenuManagerService,
        private _serviceReadyState: ServiceReadyStateService,
        private _systemMenuService: SystemMenuService,
        private translateService: TranslateService,
        private _styleManagerService: StyleManagerService,
        private _settingsService: SettingsService
    ) {

        this._badisOauthLoginService.onAuthStatusChanged.subscribe((isAuthenticated: boolean) => {

            if (isAuthenticated) {

                const authUser = this._badisOauthLoginService.userAccessToken;
                this.displayName = authUser ? authUser.displayName : 'Navizard';
                this.avatar = authUser.avatar;
                this.userName = authUser.userName;
            }

        });

        // Settings servisinden dock ayarlarını yükle
        this._serviceReadyState.isServiceReady(ServiceName.Config).subscribe(isReady => {
            if (isReady) {
                // Auto-hide ayarını yükle
                const autoHide = this._settingsService.getSettingValue<boolean>('Dock.Layout.AutoHide');
                if (autoHide !== null) {
                    this.isDockAutoHide = autoHide;
                }

                // Menü stili ayarını yükle
                const menuStyle = this._settingsService.getSettingValue<boolean>('Dock.Layout.MenuStyle.Popup');
                if (menuStyle !== null) {
                    this.isMenuPopup = menuStyle;
                }

                // Dock pozisyonu ayarını yükle - bu ayar zaten BackgroundService tarafından yönetiliyor
                // ve navBarPosition değişkeninde tutuluyor, sadece ilk değeri ayarlıyoruz
                const isDock = this._settingsService.getSettingValue<boolean>('Dock.Layout.Dock');
                if (isDock !== null) {
                    // true ise bottom, false ise top
                    const position = isDock ? 'bottom' : 'top';
                    this.backgroundService.setNavBarPosition(position);
                }

                // Tam ekran ayarını yükle
                const fullscreen = this._settingsService.getSettingValue<boolean>('Dock.Layout.Fullscreen');
                if (fullscreen !== null && fullscreen) {
                    // Eğer tam ekran ayarı true ise ve şu an tam ekranda değilsek, tam ekrana geç
                    if (!document.fullscreenElement) {
                        this.toggleFullscreen(true);
                    }
                }
            }
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.scrollbarVisibilityBugFix = true;

            // Döküman tıklamalarını dinle
            document.addEventListener('click', this.onDocumentClick.bind(this));

            // Menu servisi hazır olduğunda menü öğelerini yükle
            this._serviceReadyState.isServiceReady(ServiceName.Menu)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(isReady => {
                    if (isReady) {
                        console.log('Menu servisi hazır, menü öğeleri yükleniyor...');
                        this.loadMenuItems();
                    }
                });
            this._systemMenuService.populateSystemMenu();
        }, 100);
    }

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    ngOnInit(): void {
        // Kullanıcı menü öğelerini yükle
        this._loadUserMenuItems();

        // Add fullscreen change event listener
        document.addEventListener('fullscreenchange', this.handleFullscreenChange.bind(this));



        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                // Backend menüsünü Fuse navigation'a uygula ve dinamik öğeleri yükle (çeviri dahil)
                this._systemMenuService.populateSystemMenu();

                // Fuse nav component üzerindeki güncel (çevirisi uygulanmış) navigation'ı al
                const navComp = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
                const translated = navComp?.navigation ?? navigation.default;

                // Bileşene bağlı navigation'ı, çevirisi uygulanmış diziyle güncelle
                this.navigation = {
                    default: translated,
                    compact: translated,
                    horizontal: translated
                } as any as Navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Başlangıçta dock'u aktif olarak ayarla ve 2 saniye sonra gizle
        this.isDockActive = true;
        setTimeout(() => {
            if (!this.navigationOpened) {
                this.isDockActive = false;
            }
        }, 2000);

        // Kullanıcı tercihlerini yükle
        this.loadUserPreferences();

        // Menü stiline göre başlangıç durumlarını ayarla
        if (!this.isMenuPopup) {
            // Sidebar modu seçiliyse, sola sabitlenmiş sidebar görünecek
            this.navigationOpened = false; // Popup navigasyonunu kapalı tut
        }

        // Dil menüsü öğelerini hazırla
        this.initializeLanguageMenuItems();

        // navBarPosition değişikliklerini izle
        this.backgroundService.navBarPosition
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((position: 'top' | 'bottom' | 'left' | 'right') => {
                // Update the position based on the dock position
                if (position === 'top') {
                    this.isMenuPopup = false;
                    this._settingsService.setSettingValue('Dock.Layout.MenuStyle.Popup', false);
                    this.isDockAtBottom = false;
                } else if (position === 'bottom') {
                    this.isMenuPopup = true;
                    this._settingsService.setSettingValue('Dock.Layout.MenuStyle.Popup', true);
                    this.isDockAtBottom = true;
                } else {
                    this.isDockAtBottom = false;
                }
            });

        // Periyodik olarak dock ve overlay durumunu kontrol et (takılı kalmış durumları düzeltmek için)
        setInterval(() => {
            if (!this.navigationOpened && !this.isHovering && this.isDockActive && !this._frostMenuManager.hasOpenMenu()) {
                // Eğer dock aktif ama navigasyon kapalı, hover yok ve açık menü yoksa, dock'u deaktif yap
                this.isDockActive = false;
            }
        }, 5000); // 5 saniyede bir kontrol et


        // Açık menüleri izle ve gerekli aksiyonları al
        this._frostMenuManager.hasOpenMenu$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(hasOpenMenu => {
                console.log(`${hasOpenMenu ? 'Menü açıldı' : 'Tüm menüler kapandı'}`);

                // Menü açıkken dock'u aktif tut, kapalıyken ve hover yoksa gizle
                if (hasOpenMenu) {
                    this.activateDock();
                } else if (!this.isHovering && !this.navigationOpened) {
                    this.deactivateDock();
                }

                // İzleme için güncelle
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * Kullanıcı tercihlerini yükler
     */
    loadUserPreferences(): void {
        // Menü stilini yükle
        const isMenuPopup = this._settingsService.getSettingValue<boolean>('Dock.Layout.MenuStyle.Popup');
        if (isMenuPopup !== null && isMenuPopup !== undefined) {
            this.isMenuPopup = isMenuPopup;
        }

        // Auto-hide ayarını yükle
        const isAutoHide = this._settingsService.getSettingValue<boolean>('Dock.Layout.AutoHide');
        if (isAutoHide !== null && isAutoHide !== undefined) {
            this.isDockAutoHide = isAutoHide;
        }
    }

    ngOnDestroy(): void {
        // Timeoutları temizle
        if (this.dockHideTimeout) {
            clearTimeout(this.dockHideTimeout);
            this.dockHideTimeout = null;
        }

        // Reset timeout'u temizle
        if (this.resetOverlayTimeout) {
            clearTimeout(this.resetOverlayTimeout);
            this.resetOverlayTimeout = null;
        }

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Döküman tıklamalarını dinlemeyi bırak
        document.removeEventListener('click', this.onDocumentClick.bind(this));

        // Tam ekran değişikliğini dinlemeyi bırak
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange.bind(this));

        // Eğer tam ekran modundaysa, çık
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(err => {
                console.error(`Tam ekrandan çıkma hatası: ${err.message}`);
            });
        }
    }

    public loadActiveTagsFilter(): void {

    }

    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Menü stiline göre davranışı belirle
            if (this.isMenuPopup) {
                // Popup modunda - toggle aç/kapat
                navigation.toggle();

                // Navigasyon durumunu güncelle
                this.navigationOpened = !this.navigationOpened;

                // Navigasyon açıksa dock'u aktif tut, kapalıysa mevcut mouse pozisyonuna göre belirlemeye izin ver
                if (!this.navigationOpened) {
                    this.deactivateDock();
                } else {
                    this.activateDock();
                }
            } else {
                // Sidebar modunda - otomatik kapanmaz, hamburger butonu mainNavigation'ı toggle eder
                // Bu durumda sadece sabit sidebar'ı veya popup'ı açıp kapamak istiyoruz
                navigation.toggle();
                this.navigationOpened = !this.navigationOpened;
            }
        }
    }

    /**
     * Dock'u aktif hale getirir ve zamanlayıcıyı temizler
     */
    activateDock(): void {
        // Eğer dock kapalıysa, aktif hale getir
        if (!this.isDockActive) {
            this.isDockActive = true;
        }

        // Varsa mevcut zamanlayıcıyı temizle
        if (this.dockHideTimeout) {
            clearTimeout(this.dockHideTimeout);
            this.dockHideTimeout = null;
        }

        // Reset timeout'u temizle
        if (this.resetOverlayTimeout) {
            clearTimeout(this.resetOverlayTimeout);
            this.resetOverlayTimeout = null;
        }
    }

    /**
     * Dock'u deaktif hale getirir (gecikmeli)
     */
    deactivateDock(): void {
        // Otomatik gizleme kapalıysa bir şey yapma
        if (!this.isDockAutoHide) {
            return;
        }

        // Bir menü açıksa dock'u asla gizleme
        if (this._frostMenuManager.hasOpenMenu()) {
            console.log('Dock gizlenmedi: Açık menü var');
            return;
        }

        // Navigasyon açıksa dock'u gizleme
        if (this.navigationOpened) {
            console.log('Dock gizlenmedi: Navigasyon açık');
            return;
        }

        // Üzerinde hover varsa gizleme
        if (this.isHovering) {
            console.log('Dock gizlenmedi: Hover durumunda');
            return;
        }

        // Herhangibir engel yoksa dock'u gizle
        console.log('Dock gizleniyor');
        this.isDockActive = false;
    }

    /**
     * Dock alanına mouse girdiğinde
     */
    onDockMouseEnter(): void {
        this.isHovering = true;
        this.activateDock();

        // Eğer resetOverlayTimeout varsa temizle
        if (this.resetOverlayTimeout) {
            clearTimeout(this.resetOverlayTimeout);
            this.resetOverlayTimeout = null;
        }
    }

    /**
     * Dock alanından mouse çıktığında
     */
    onDockMouseLeave(): void {
        this.isHovering = false;
        this.deactivateDock();
    }

    /**
     * Overlay'i ve dock'u güvenli şekilde sıfırlar
     * Takılı kalan hover olayları için güvenlik mekanizması
     */
    resetOverlayAndDock(): void {
        // Eğer navigasyon açık değilse, hover olmuyorsa ve açık menü yoksa overlay ve dock'u gizle
        if (!this.navigationOpened && !this.isHovering && !this._frostMenuManager.hasOpenMenu()) {
            this.isDockActive = false;

            // Reset timeout'u temizle
            if (this.resetOverlayTimeout) {
                clearTimeout(this.resetOverlayTimeout);
                this.resetOverlayTimeout = null;
            }
        }
    }

    public photoLink(photoId: string): any {
        return this.dataService.getAvatarPhoto(photoId);
    }

    public errorHandler(event: any): any {
        event.target.src = './assets/images/logo/navizard.png';
    }

    public backErrorHandler(event: any): any {
        event.target.src = './assets/images/logo/navizard.png';
    }

    /**
     * Menü stilini popup/sidebar arasında değiştirir
     */
    toggleMenuStyle(): void {
        // Menü stilini değiştir
        this.isMenuPopup = !this.isMenuPopup;

        // Kullanıcı tercihini SettingsService ile kaydet
        this._settingsService.setSettingValue('Dock.Layout.MenuStyle.Popup', this.isMenuPopup);

        // Menü değişikliğini uygula
        this._updateMenuStyle();
    }

    /**
     * Menü stilini günceller ve ilgili ayarlamaları yapar
     * @private
     */
    private _updateMenuStyle(): void {
        // Mod değiştirildiğinde navigasyon durumunu sıfırla
        // Eğer navigasyon açıksa, kapat
        if (this.navigationOpened) {
            const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
            if (navigation && this.isMenuPopup) {
                navigation.close();
            }
            this.navigationOpened = false;
        }
    }

    /**
     * Mouse dock alanına girdiğinde
     */
    mouseEnter(): void {
        this.isHovering = true;
        this.isDockActive = true;

        // Varsa mevcut zamanlayıcıyı temizle
        if (this.dockHideTimeout) {
            clearTimeout(this.dockHideTimeout);
            this.dockHideTimeout = null;
        }
    }

    /**
     * Mouse dock alanından çıktığında
     */
    mouseLeave(): void {
        this.isHovering = false;

        // Navigasyon açıksa dock'u gizleme
        if (this.navigationOpened) {
            return;
        }

        // Herhangi bir popup menu açıksa dock'u gizleme
        if (this._frostMenuManager.hasOpenMenu()) {
            return;
        }

        // Dock'u gizle (kısa gecikme ile)
        this.dockHideTimeout = setTimeout(() => {
            if (!this.isHovering && !this.navigationOpened && !this._frostMenuManager.hasOpenMenu()) {
                this.isDockActive = false;
            }
            this.dockHideTimeout = null;
        }, 300);
    }

    /**
     * Dock popup menüsünü aç/kapat
     * @param menuId Menü kimliği (örn: 'tools', 'user')
     * @param event Mouse tıklama olayı - menü konumlandırması için kullanılır
     */
    toggleDockMenu(menu: 'tools' | 'user' | 'reports' | 'settings' | 'charts', event: MouseEvent) {
        event.stopPropagation();
        this.activeDockMenu = this.activeDockMenu === menu ? null : menu;
    }

    /**
     * Tüm açık menüleri kapatır
     */
    private closeAllMenus(): void {
        // HTML class'larını güncellemenin yanında, CSS stillerini de sıfırla
        const menuIds = ['tools', 'user', 'reports'];

        menuIds.forEach(id => {
            const menu = document.getElementById(`${id}-menu`);
            if (menu) {
                menu.classList.remove('open');
                menu.classList.add('closed');
                menu.style.visibility = 'hidden';
                menu.style.opacity = '0';
            }
        });

        // activeDockMenu değişkenini null olarak ayarla
        // this.activeDockMenu = null; // Bu satırı kaldırdık, çünkü her zaman gerekli değil
    }


    /**
     * Menü öğesi tıklaması işleyicisi
     * @param item Tıklanan menü öğesi
     */
    onMenuItemClick(item: DockMenuItem): void {
        // Menüyü kapat
        this.activeDockMenu = null;

        if (item.action) {
            // Özel eylem varsa çalıştır
            item.action();
        } else if (item.route) {
            // Route varsa yönlendir
            this._router.navigate([item.route]);
        }
    }

    /**
     * Dock popup menüsünden çıkış işlevi
     */
    logout(): void {
        this._badisOauthLoginService.logout();
    }

    /**
     * Tıklama olayını algıla ve açık dock menüsünü kapat
     */
    onDocumentClick(event: MouseEvent): void {
        // Açık menü yoksa işlem yapma
        if (!this.activeDockMenu) {
            return;
        }

        // Aktif menü elementini bul
        const activeMenuId = this.activeDockMenu;
        const activeMenu = document.getElementById(`${activeMenuId}-menu`);

        // Dock elementini bul
        const dockElement = document.querySelector('.dock-auto-hide');

        // Eğer tıklanan eleman menü içinde veya dock içinde değilse menüyü kapat
        if (activeMenu && !activeMenu.contains(event.target as Node) &&
            dockElement && !dockElement.contains(event.target as Node)) {
            console.log('Document click outside menu - closing all menus');
            this.activeDockMenu = null;
            this.closeAllMenus();
        }
    }

    /**
     * Menü öğelerinden dock menüsü için uygun olanları toplar
     */
    collectDockableItems(items: FuseNavigationItem[], targetList: DockMenuItem[]): void {
        if (!items || !items.length) {
            return;
        }

        items.forEach(item => {
            // Sadece temel ve tıklanabilir öğeleri al (divider, spacer vb. değil)
            if (item.type === 'basic' && item.icon && item.link && !item.disabled) {
                // Menü öğesinden dock öğesi oluştur
                targetList.push({
                    id: item.id || item.title?.toLowerCase().replace(/\s+/g, '-') || 'menu-item',
                    icon: (item.icon.replace('fal:', '') || 'circle') as IconName,
                    label: item.title || 'Menu Item',
                    route: item.link || '#'
                });
            }

            // Eğer alt öğeler varsa onları da işle (en fazla 8 öğe al)
            if (item.type === 'collapsable' && item.children) {
                this.collectDockableItems(item.children, targetList);
            }

            // Listeyi en fazla 8 öğeyle sınırla (menü çok uzun olabilir)
            if (targetList.length >= 8) {
                return;
            }
        });
    }

    /**
     * Menü öğesi için icon formatını düzeltir
     */
    getIconFromNavigationItem(icon: string): IconName {
        if (!icon) {
            return 'circle' as IconName;
        }

        // 'fal:home' formatındaki ikonları düzelt
        if (icon.includes(':')) {
            return icon.split(':')[1] as IconName;
        }

        return icon as IconName;
    }

    /**
     * Dock auto-hide özelliğini açıp kapatır ve bu ayarı localStorage'da saklar
     */
    toggleDockAutoHide(): void {
        this.dockOperation(DockOperation.TOGGLE_AUTO_HIDE);
    }

    /**
     * Dock pozisyonunu değiştirir (üst/alt)
     */
    toggleDockPosition(): void {
        this.dockOperation(DockOperation.TOGGLE_POSITION);
    }



    /**
     * Belirli bir ayarın değerini döndürür
     * @param settingId Ayar ID'si
     * @returns Ayarın değeri (boolean)
     */
    getSettingValue(settingId: string): boolean {
        switch (settingId) {
            case DockOperation.TOGGLE_MENU_STYLE:
                return !this.isMenuPopup; // Kenar çubuğu görünümü açıksa true

            case DockOperation.TOGGLE_AUTO_HIDE:
                return this.isDockAutoHide; // Auto-hide açıksa true

            case DockOperation.TOGGLE_POSITION:
                // NavBar pozisyonunu kontrol et
                let isTop = false;
                this.backgroundService.navBarPosition.pipe(take(1)).subscribe(position => {
                    isTop = position === 'top';
                });
                return isTop; // Üst pozisyondaysa true

            case DockOperation.TOGGLE_FULLSCREEN:
                return this.isFullscreen; // Tam ekran modundaysa true

            default:
                return false;
        }
    }

    // Menü stili için kullanılan özellik (true = kenar çubuğu, false = popup)
    get menuStyleValue(): boolean {
        return !this.isMenuPopup;
    }

    set menuStyleValue(value: boolean) {
        if (this.isMenuPopup === value) {
            this.toggleMenuStyle();
        }
    }

    // Dock'un üst pozisyonda olup olmadığını döndüren hesaplanmış özellik
    get isDockAtTop(): boolean {
        let isTop = false;
        this.backgroundService.navBarPosition.pipe(take(1)).subscribe(position => {
            isTop = position === 'top';
        });
        return isTop;
    }

    set isDockAtTop(value: boolean) {
        // Mevcut değeri kontrol et ve farklıysa değiştir
        this.backgroundService.navBarPosition.pipe(take(1)).subscribe(position => {
            const currentIsTop = position === 'top';
            if (currentIsTop !== value) {
                this.toggleDockPosition();
            }
        });
    }

    /**
     * Belirli bir ayarı aç/kapat
     * @param settingId Ayar ID'si
     * @param value Yeni değer
     */
    toggleSetting(settingId: string, value: any): void {
        console.log(`toggleSetting: ${settingId} = ${value}`);
        // Event nesnesini boolean'a çevir
        const boolValue = value === true || value === 'true' || value === 1;

        switch (settingId) {
            case 'menu-style':
                // Menü stilini değiştir (value true ise kenar çubuğu, false ise popup)
                // Not: menuStyleValue setter'i bu işlemi otomatik olarak yapıyor
                this._settingsService.setSettingValue('Dock.Layout.MenuStyle.Popup', !boolValue);
                break;

            case 'auto-hide':
                // Auto-hide özelliğini değiştir
                this.isDockAutoHide = boolValue;
                this._settingsService.setSettingValue('Dock.Layout.AutoHide', boolValue);
                break;

            case 'dock-position':
                // NavBar pozisyonunu değiştir
                // Not: isDockAtTop setter'i bu işlemi otomatik olarak yapıyor
                this._settingsService.setSettingValue('Dock.Layout.Dock', !boolValue);
                break;

            case 'fullscreen':
                // Tam ekran modunu değiştir
                this.toggleFullscreen(boolValue);
                this._settingsService.setSettingValue('Dock.Layout.Fullscreen', boolValue);
                break;
        }

    }

    /**
     * Dock işlemlerini gerçekleştiren ortak metod
     * @param operation İşlem türü
     */
    dockOperation(operation: DockOperation): void {
        console.log(`Dock işlemi çalıştırılıyor: ${operation}`);

        switch (operation) {
            case DockOperation.TOGGLE_MENU_STYLE:
                this.toggleMenuStyle();
                break;

            case DockOperation.TOGGLE_AUTO_HIDE:
                // Auto-hide özelliğini değiştir
                this.isDockAutoHide = !this.isDockAutoHide;
                this._settingsService.setSettingValue('Dock.Layout.AutoHide', this.isDockAutoHide);
                console.log("Auto-hide yeni durum:", this.isDockAutoHide);
                break;

            case DockOperation.TOGGLE_POSITION:
                // Mevcut pozisyonu al ve değiştir
                this.backgroundService.navBarPosition.pipe(take(1)).subscribe(currentPosition => {
                    console.log('Mevcut pozisyon:', currentPosition);

                    // Pozisyonu değiştir
                    this.backgroundService.toggleNavBarPosition();

                    // Pozisyon değiştiğinde bileşeni yeniden yapılandır
                    setTimeout(() => {
                        this.backgroundService.navBarPosition.pipe(take(1)).subscribe(newPosition => {
                            console.log('Yeni pozisyon:', newPosition);

                            if (newPosition === 'top') {
                                // Yukarı pozisyona geçince popup menüyü kapatıyoruz
                                this.navigationOpened = false;
                                this.isMenuPopup = false;
                                this.isDockActive = false;
                            } else {
                                // Aşağı pozisyona geçince popup menüyü aktifleştiriyoruz
                                this.isMenuPopup = true;
                                this.isDockActive = true;

                                // 3 saniye sonra gizle
                                if (this.dockHideTimeout) {
                                    clearTimeout(this.dockHideTimeout);
                                }

                                this.dockHideTimeout = setTimeout(() => {
                                    if (!this.navigationOpened && !this.isHovering) {
                                        this.isDockActive = false;
                                    }
                                    this.dockHideTimeout = null;
                                }, 3000);
                            }

                            // Görünümü zorla güncelle
                            this._changeDetectorRef.markForCheck();
                            this._changeDetectorRef.detectChanges();
                        });
                    }, 100);
                });
                break;

            case DockOperation.TOGGLE_FULLSCREEN:
                // Tam ekran modunu değiştir
                this.toggleFullscreen(!this.isFullscreen);
                this._settingsService.setSettingValue('Dock.Layout.Fullscreen', !this.isFullscreen);
                break;
        }

        // Aktif menüyü kapat
        this.activeDockMenu = null;
    }

    /**
     * Live görünümüne yönlendir
     */
    navigateToLive(): void {
        this._router.navigate(['/live']);
    }

    /**
     * Dil değiştirme işlemini gerçekleştirir
     */
    toggleLanguage(): void {
        console.log("Dil değiştirme ekranı açıldı");
        // Burada dil değiştirme işlemleri yapılacak
    }

    // Tam ekran durumunu takip eden değişken
    isFullscreen: boolean = false;

    /**
     * Tam ekran modunu aç/kapat
     * @param forceState İsteğe bağlı - istenen tam ekran durumunu zorla belirle
     */
    toggleFullscreen(forceState?: boolean): void {
        const elem = document.documentElement;

        // Eğer forceState belirtilmişse, o duruma göre ayarla
        if (forceState !== undefined) {
            if (forceState && !document.fullscreenElement) {
                elem.requestFullscreen().catch(err => {
                    console.error(`Tam ekran hatası: ${err.message}`);
                });
                this.isFullscreen = true;
            } else if (!forceState && document.fullscreenElement) {
                document.exitFullscreen();
                this.isFullscreen = false;
            }
            return;
        }

        // Eğer forceState belirtilmemişse, mevcut durumun tersini ayarla
        if (!document.fullscreenElement) {
            elem.requestFullscreen().catch(err => {
                console.error(`Tam ekran hatası: ${err.message}`);
            });
            this.isFullscreen = true;
        } else {
            document.exitFullscreen();
            this.isFullscreen = false;
        }
    }

    /**
     * FrostOverlayMenu'den seçilen menü öğesini işler
     */
    onFrostMenuItemClick(item: FrostMenuItem): void {
        console.log('Seçilen menü öğesi:', item);

        // Önce dock operasyonları için kontrol et
        if (Object.values(DockOperation).includes(item.id as DockOperation)) {
            this.dockOperation(item.id as DockOperation);
            return;
        }

        // Dil değiştirme işlemi
        if (item.id && item.id.startsWith('language-')) {
            // language-tr, language-en gibi ID'den dil kodunu çıkar
            const langKey = item.id.substring(9);
            this.languageService.setLanguage(langKey);
            this.initializeLanguageMenuItems();
            return;
        }

        // Diğer menü öğeleri için işlem
        if (item.link) {
            this._router.navigate([item.link]);
        }

        if (item.action) {
            item.action(item);
        }
    }

    /**
     * Dil menü öğelerini günceller
     */
    updateLanguageMenuItems(): void {
        // Dil listesini servisten al
        const languages = this.languageService.availableLanguages;

        // Dil menü öğelerini oluştur
        this.languageMenuItems = languages.map(lang => ({
            id: `language-${lang.key}`,
            label: lang.title,
            icon: ['fal', 'flag'],
            description: lang.key === 'tr' ? 'Türkçe dili seç' : 'Select English language',
            badge: this.languageService.selectedLanguage?.key === lang.key ? '✓' : null,
            badgeColor: 'text-green-500 font-bold'
        }));

        this._changeDetectorRef.markForCheck();
    }

    /**
     * Dil menüsü öğelerini hazırla
     */
    initializeLanguageMenuItems(): void {
        // Dil menüsü öğelerini hazırla
        this.updateLanguageMenuItems();
    }

    /**
     * Bir Frost menüsü açıldığında
     */
    onFrostMenuOpened(): void {
        console.log('Frost menüsü açıldı');
        this.activateDock();
    }

    /**
     * Bir Frost menüsü kapandığında
     */
    onFrostMenuClosed(): void {
        console.log('Frost menüsü kapandı');

        // Eğer hiç menü kalmadıysa ve fare üzerinde değilse dock'u gizle
        if (!this._frostMenuManager.hasOpenMenu() && !this.isHovering && !this.navigationOpened) {
            this.deactivateDock();
        }
    }

    /**
     * Menu servisi hazır olduğunda gerekli menü öğelerini yükler
     */
    loadMenuItems(): void {
        // Menü öğelerini ayrı ayrı yükle
        this.loadExternalApps();
        this.loadReports();

        // Değişiklikleri uygula
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Harici uygulamaları yükler ve FrostMenuItem formatına dönüştürür
     */
    private loadExternalApps(): void {
        // SystemMenuService'den ham veriyi al
        const externalApps = this._systemMenuService.getRawExternalApps();

        // Eğer hiç uygulama yoksa
        if (!externalApps || externalApps.length === 0) {
            this.externalAppsFrostMenuItems = [{
                id: 'no-apps',
                label: 'Harici uygulama bulunamadı',
                icon: ['fal', 'info-circle'],
                disabled: true
            }];
            return;
        }

        // FrostMenuItem'a dönüştür
        const menuItems = [];


        // Her uygulamayı ekle
        externalApps.forEach(app => {
            menuItems.push({
                id: app.id.toString(),
                label: app.menuTitle || app.title,
                icon: this.getExternalAppIcon(app),
                //description: app.menuSubtitle,
                link: `/apps/${app.id}`,
                labelColor: this.getLabelColor(app.menuTitleColor),
                iconColor: this.getIconColor(app.menuIconColor)
            });
        });

        this.externalAppsFrostMenuItems = menuItems;
    }

    /**
     * Raporları yükler ve FrostMenuItem formatına dönüştürür
     */
    private loadReports(): void {

        // SystemMenuService'den ham veriyi al
        const reportData = this._systemMenuService.getNavigationReports();

        // FrostMenuItem'a dönüştür
        const menuItems = [];

        // Eğer veriler boşsa veya uygun formatta değilse
        if (!reportData || !reportData.children) {
            this.reportsFrostMenuItems = [{
                id: 'no-reports',
                label: 'Rapor bulunamadı',
                icon: ['fal', 'info-circle'],
                disabled: true
            }];
            return;
        }

        // Ana rapor grubunun children elementlerini işle
        if (reportData.children && reportData.children.length > 0) {
            // Tüm collapsable grupları işle
            reportData.children.forEach(group => {
                if (group.type === 'collapsable' && group.children) {
                    // Grup başlığı
                    menuItems.push(createMenuSeparator(
                        group?.title ? this.translateService.instant(group?.title) : '',
                        group?.subtitle ? this.translateService.instant(group.subtitle) : ''
                    ));

                    // Gruptaki basic raporları ekle
                    if (group.children && group.children.length > 0) {
                        group.children.forEach(report => {
                            if (report.type === 'basic' && !report.hide) {
                                menuItems.push(this.createReportMenuItem(report, group.id === 'custom-reports'));
                            }
                        });
                    }
                } else if (group.type === 'basic' && !group.hide) {
                    // Ana seviyedeki basic raporları ekle
                    menuItems.push(this.createReportMenuItem(group, false));
                }
            });
        }

        this.reportsFrostMenuItems = menuItems;
        console.log(`${menuItems.length} adet rapor öğesi yüklendi`);
    }

    /**
     * Rapor öğeleri için FrostMenuItem oluşturur
     */
    private createReportMenuItem(report: any, isCustomReport: boolean = false): any {
        // İkon formatı
        const icon = this.getFormattedIcon(report.icon);

        // Badge bilgilerini ayarla
        let badge = null;
        let badgeColor = null;
        if (report.badge) {
            badge = report.badge.title;
            badgeColor = report.badge.classes;
        }

        // Renk sınıflarını ayarla
        let labelColor = '';
        let iconColor = '';

        if (report.classes) {
            if (report.classes.title) {
                labelColor = report.classes.title;
            }
            if (report.classes.wrapper) {
                iconColor = report.classes.wrapper;
            }
        }

        // Rapor menü öğesini oluştur
        const reportItem = {
            id: report.id,
            label: this.translateService.instant(report.title),
            icon: icon,
            description: report.subtitle ? this.translateService.instant(report.subtitle) : '',
            link: report.link,
            labelColor: labelColor,
            iconColor: iconColor,
            badge: badge,
            badgeColor: badgeColor,
            // Ek özellikler
            disabled: report.disabled || false,
            exactMatch: report.exactMatch || false
        };

        return reportItem;
    }

    /**
     * İkon formatını düzenler
     */
    private getFormattedIcon(icon: any): any {
        if (!icon) {
            return ['fal', 'file-chart-column'];
        }

        // Eğer icon bir dizi ise (ör: ['fal', 'chart-line']), doğrudan döndür
        if (Array.isArray(icon)) {
            return icon;
        }

        // Eğer icon bir string ve formatı doğruysa (ör: "fal:chart-line"), dizi formatına çevir
        if (typeof icon === 'string') {
            if (icon.includes(':')) {
                const parts = icon.split(':');
                return [parts[0], parts[1]];
            } else if (icon.startsWith('fal') || icon.startsWith('fas') || icon.startsWith('far')) {
                // Basit fontawesome icon string'i
                return ['fal', icon.replace('fal', '').replace('fas', '').replace('far', '')];
            }
        }

        // Diğer durumlarda varsayılan ikon döndür
        return ['fal', 'file-chart-column'];
    }

    /**
     * Harici uygulama ikonu döndürür
     */
    private getExternalAppIcon(app: any): any {
        // Eğer ikonu yoksa varsayılan ikon kullan
        if (!app.menuIcon && !app.icon) {
            return ['fal', 'window-restore'];
        }

        const iconStr = app.menuIcon || app.icon;

        return this.getFormattedIcon(iconStr);
    }

    /**
     * Etiket rengi CSS sınıfı oluşturur
     */
    private getLabelColor(colorCode: string): string {
        if (!colorCode) {
            return '';
        }

        // Eğer CSS class listesiyse aynen kullan
        if (colorCode.includes(' ')) {
            return colorCode;
        }

        // Style manager yüklenmemişse veya null ise varsayılan kullan
        if (!this._styleManagerService) {
            return 'text-blue-500';
        }

        return this._styleManagerService.addColorClass(colorCode);
    }

    /**
     * İkon rengi CSS sınıfı oluşturur
     */
    private getIconColor(colorCode: string): string {
        if (!colorCode) {
            return '';
        }

        // Eğer CSS class listesiyse aynen kullan
        if (colorCode.includes(' ')) {
            return colorCode;
        }

        // Style manager yüklenmemişse veya null ise varsayılan kullan 
        if (!this._styleManagerService) {
            return 'text-blue-500';
        }

        return this._styleManagerService.addColorClass(colorCode);
    }

    /**
     * Tam ekran değişikliklerini işler - tarayıcı taraflı değişiklik olduğunda state'i günceller
     */
    handleFullscreenChange(): void {
        // Tam ekran durumunu tarayıcının bildirdiği durumla eşitle
        this.isFullscreen = !!document.fullscreenElement;


    }

}